import React, { useState, useEffect } from "react";
import { Notifications, Email, Sms, Close } from "@mui/icons-material";
import apiService from "../../services/apiService";

const NotificationsTab = ({ loanId }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);

  useEffect(() => {
    if (loanId) {
      fetchNotifications();
    }
  }, [loanId]);

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(
        `${process.env.REACT_APP_API_HOST_NOTIFICATIONS}/loans/notifications`,
        { loanId }
      );

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      } else {
        setError("Failed to load notifications");
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError("An error occurred while loading notifications");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return "N/A";
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return "";
    // Strip HTML tags for preview
    const stripped = text.replace(/<[^>]+>/g, '');
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength) + "...";
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'email':
        return <Email fontSize="small" className="text-blue-600" />;
      case 'sms':
      case 'text':
        return <Sms fontSize="small" className="text-green-600" />;
      default:
        return <Notifications fontSize="small" className="text-gray-600" />;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'email':
        return 'bg-blue-100 text-blue-800';
      case 'sms':
      case 'text':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Loading notifications...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-12 text-center">
        <Notifications className="mx-auto text-red-400 mb-4" style={{ fontSize: 64 }} />
        <h3 className="text-lg font-semibold text-red-700 mb-2">Error Loading Notifications</h3>
        <p className="text-red-500">{error}</p>
        <button
          onClick={fetchNotifications}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!notifications || notifications.length === 0) {
    return (
      <div className="p-12 text-center">
        <Notifications className="mx-auto text-gray-400 mb-4" style={{ fontSize: 64 }} />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No Notifications</h3>
        <p className="text-gray-500">
          No notifications have been sent for this loan yet.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Notification History ({notifications.length})
        </h3>
      </div>

      {/* Notifications Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent At
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Body
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {notifications.map((notification, index) => (
                <tr key={notification._id || index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {index + 1}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(notification.createdAt)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(notification.type)}
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadgeColor(notification.type)}`}>
                        {notification.type || 'N/A'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 max-w-xs">
                    <div className="font-medium truncate">
                      {notification.subject || "No Subject"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-md">
                    <div className="truncate">
                      {truncateText(notification.document || notification.body)}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <button
                      onClick={() => setSelectedNotification(notification)}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notification Detail Modal */}
      {selectedNotification && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="border-b p-6 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Notification Details</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Sent on {formatDate(selectedNotification.createdAt)}
                </p>
              </div>
              <button
                onClick={() => setSelectedNotification(null)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Close />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Type</p>
                  <div className="flex items-center gap-2">
                    {getTypeIcon(selectedNotification.type)}
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadgeColor(selectedNotification.type)}`}>
                      {selectedNotification.type || 'N/A'}
                    </span>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Subject</p>
                  <p className="text-base font-semibold text-gray-900">
                    {selectedNotification.subject || "No Subject"}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Message Body</p>
                  <div
                    className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: selectedNotification.document || selectedNotification.body || "No content"
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="border-t p-4 flex justify-end">
              <button
                onClick={() => setSelectedNotification(null)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;
